import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { Card, CardContent } from "@/shared/components/ui/card";
import {
  FloatNumberInput,
  IntNumberInput,
} from "@/shared/components/domain/qpm/inputs/number-input";
import { useGfsOfferConfigGetQuery } from "@/services/backend/gfs/offer/api";
import {
  GfsOfferDocumentRow,
  GfsOfferExpertTraining,
  GfsOfferTextBlock,
} from "@/services/backend/gfs/offer/types";
import {
  GfsOfferTextAddButton,
  GfsOfferTextBlocksEditor,
} from "@/services/backend/gfs/offer/components/offer-config-editor";
import { useGfsOfferEditorContext } from "@/services/backend/gfs/offer/components/offer-editor-context";

type OfferTextRowKey =
  | "situation"
  | "services"
  | "investment"
  | "timeWindow"
  | "appointment"
  | "customerPreparation"
  | "customerSupport"
  | "products"
  | "warranty"
  | "importantNotes"
  | "agreement";

const offerTextRowKeys: OfferTextRowKey[] = [
  "situation",
  "services",
  "investment",
  "timeWindow",
  "appointment",
  "customerPreparation",
  "customerSupport",
  "products",
  "warranty",
  "importantNotes",
  "agreement",
];
const offerTextRowKeySet = new Set<string>(offerTextRowKeys);

export function GfsOfferDocumentPage() {
  const { offer, updateContent, updateOfferDocument, saveState } =
    useGfsOfferEditorContext();
  const document = offer.offerDocument;
  const {
    data: offerConfig,
    error: offerConfigError,
    isLoading: offerConfigLoading,
  } = useGfsOfferConfigGetQuery({ companyId: offer.companyId });

  if (offerConfigError) {
    return <RTKQueryErrorAlert error={offerConfigError} />;
  }
  if (offerConfigLoading || !offerConfig) {
    return (
      <div className="text-muted-foreground p-4">Lade Angebotstexte …</div>
    );
  }

  const updateDocumentRow = (
    key: OfferTextRowKey,
    patch: Partial<GfsOfferDocumentRow>,
  ) => {
    const existingRow = document.rows.find((row) => row.key === key);
    const configRow = offerConfig.rows.find((row) => row.key === key);
    const nextRow = {
      ...(existingRow ?? getDefaultDocumentRow(key, configRow?.order)),
      ...patch,
    };
    const rows = existingRow
      ? document.rows.map((row) => (row.key === key ? nextRow : row))
      : [...document.rows, nextRow];
    updateOfferDocument({ ...document, rows });
  };

  const addLibraryBlock = (sectionKey: string, blockId: string) => {
    if (!isOfferTextRowKey(sectionKey)) return;
    const sourceRow = offerConfig.rows.find((row) => row.key === sectionKey);
    const sourceBlock = sourceRow?.blocks.find((block) => block.id === blockId);
    if (!sourceBlock) return;

    const existingRow = document.rows.find((row) => row.key === sectionKey);
    const row =
      existingRow ?? getDefaultDocumentRow(sectionKey, sourceRow?.order);
    const blocks = row.blocks ?? [];
    const nextBlocks = blocks.some((block) => block.id === blockId)
      ? blocks.map((block) =>
          block.id === blockId ? { ...block, selected: true } : block,
        )
      : [...blocks, { ...sourceBlock, selected: true }];
    updateDocumentRow(sectionKey, { blocks: nextBlocks, visible: true });
  };

  const addCustomBlock = (sectionKey: string) => {
    if (!isOfferTextRowKey(sectionKey)) return;
    const row =
      document.rows.find((candidate) => candidate.key === sectionKey) ??
      getDefaultDocumentRow(
        sectionKey,
        offerConfig.rows.find((candidate) => candidate.key === sectionKey)
          ?.order,
      );
    updateDocumentRow(sectionKey, {
      visible: true,
      blocks: [
        ...(row.blocks ?? []),
        {
          id: `custom-${crypto.randomUUID()}`,
          title: "Neuer Textblock",
          body: "<p></p>",
          selected: true,
          position: "after",
        },
      ],
    });
  };

  const rows = offerTextRowKeys
    .map((rowKey, index) => {
      const documentRow = document.rows.find((row) => row.key === rowKey);
      const configRow = offerConfig.rows.find((row) => row.key === rowKey);
      return {
        ...getDefaultDocumentRow(
          rowKey,
          configRow?.order ?? documentRow?.order ?? 1000 + index,
        ),
        ...documentRow,
        ...(configRow
          ? { label: configRow.label, order: configRow.order }
          : {}),
        key: rowKey,
      };
    })
    .sort((left, right) => left.order - right.order);

  return (
    <div className="space-y-4 pb-16">
      {saveState.error && <RTKQueryErrorAlert error={saveState.error} />}

      <div className="flex items-center justify-between gap-4 border-b pb-4">
        <h1 className="text-xl font-semibold">{t("Angebotstexte")}</h1>
        <PersistenceStateUsingContext className="shrink-0" />
      </div>

      <div className="flex justify-center">
        <GfsOfferTextAddButton
          config={offerConfig}
          disabled={offer.immutable}
          onAddLibraryBlock={addLibraryBlock}
          onAddCustomBlock={(sectionKey) => {
            if (sectionKey) addCustomBlock(sectionKey);
          }}
        />
      </div>

      {rows.map((row) => {
        const automaticBlock = hasRenderableHTML(row.value)
          ? [createAutomaticBlock(row)]
          : [];
        const libraryBlocks =
          offerConfig.rows.find((candidate) => candidate.key === row.key)
            ?.blocks ?? [];

        return (
          <Card key={row.key}>
            <CardContent className="p-5">
              <GfsOfferTextBlocksEditor
                title={row.label}
                blocks={[...automaticBlock, ...(row.blocks ?? [])]}
                libraryBlocks={libraryBlocks}
                disabled={offer.immutable}
                onChange={(blocks) => {
                  const automatic = blocks.find(
                    (block) => block.id === automaticBlock[0]?.id,
                  );
                  const nextBlocksWithoutAutomatic = blocks.filter(
                    (block) => block.id !== automaticBlock[0]?.id,
                  );
                  const automaticWasEdited =
                    automaticBlock.length > 0 &&
                    automatic != null &&
                    automatic.selected &&
                    !isUnchangedAutomaticBlock(automatic, row);
                  const nextBlocks = automaticWasEdited
                    ? [...nextBlocksWithoutAutomatic, automatic]
                    : nextBlocksWithoutAutomatic;
                  const nextValue =
                    automatic?.selected && !automaticWasEdited
                      ? automatic.body
                      : automaticBlock.length > 0
                        ? ""
                        : row.value;
                  const nextAutomatic =
                    automaticBlock.length > 0
                      ? Boolean(automatic?.selected) &&
                        !automaticWasEdited &&
                        row.automatic
                      : row.automatic;
                  updateDocumentRow(row.key, {
                    blocks: nextBlocks,
                    value: nextValue,
                    automatic: nextAutomatic,
                    visible: true,
                  });
                }}
              />
              {row.key === "investment" && (
                <ExpertTrainingFields
                  value={offer.content.expertTraining}
                  disabled={offer.immutable}
                  onChange={(expertTraining) =>
                    updateContent({ expertTraining })
                  }
                />
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function createAutomaticBlock(row: GfsOfferDocumentRow): GfsOfferTextBlock {
  return {
    id: `automatic-${row.key}`,
    title: row.automatic ? "Automatisch" : "Manueller Inhalt",
    body: row.value ?? "",
    selected: true,
    position: "after",
  };
}

function isUnchangedAutomaticBlock(
  block: GfsOfferTextBlock,
  row: GfsOfferDocumentRow,
) {
  return (
    block.selected &&
    block.title === (row.automatic ? "Automatisch" : "Manueller Inhalt") &&
    block.body === (row.value ?? "") &&
    block.position === "after" &&
    !block.pestGroupIds?.length &&
    !block.pestCategoryIds?.length &&
    !block.pestIndicatorIds?.length
  );
}

function hasRenderableHTML(value?: string) {
  return (
    (value ?? "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .trim().length > 0
  );
}

function isOfferTextRowKey(value: string): value is OfferTextRowKey {
  return offerTextRowKeySet.has(value);
}

function getDefaultDocumentRow(
  key: OfferTextRowKey,
  order = 1000,
): GfsOfferDocumentRow {
  const labels: Record<OfferTextRowKey, string> = {
    situation: "Ist-Situation:",
    services: "Leistung:",
    investment: "Investition:",
    timeWindow: "Zeitfenster:",
    appointment: "Durchführungs-Termin:",
    customerPreparation: "Vorbereitung durch den Auftraggeber:",
    customerSupport: "Unterstützung durch den Auftraggeber:",
    products: "Präparate:",
    warranty: "Gewährleistung:",
    importantNotes: "Wichtige Hinweise:",
    agreement: "Einverständniserklärung:",
  };
  return {
    key,
    label: labels[key],
    visible: true,
    order,
    blocks: [],
    automatic: true,
    value: "",
  };
}

function ExpertTrainingFields({
  value,
  disabled,
  onChange,
}: {
  value: GfsOfferExpertTraining;
  disabled: boolean;
  onChange: (value: GfsOfferExpertTraining) => void;
}) {
  return (
    <div className="grid gap-3 border-l-2 pl-4 lg:grid-cols-[220px_minmax(0,1fr)] lg:items-center">
      <div className="text-sm font-semibold">{t("Sachkundeschulung")}</div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <label className="flex items-center gap-2 text-sm font-medium">
          {t("Personen")}
          <IntNumberInput
            className="w-24"
            value={value.participants}
            disabled={disabled}
            onChange={(participants) => onChange({ ...value, participants })}
          />
        </label>
        <label className="flex items-center gap-2 text-sm font-medium">
          {t("Preis")}
          <FloatNumberInput
            className="w-28"
            value={value.price}
            disabled={disabled}
            onChange={(price) =>
              onChange({ ...value, price, enabled: price > 0 })
            }
          />
          <span className="text-sm">€</span>
        </label>
      </div>
    </div>
  );
}
